import { lazy as reactLazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useGlossaryStore } from './stores/glossaryStore'
// SPEC-NEVER-STUCK-AUTH-AND-CHUNK-01 (D-230) B1/B2: 全ての lazy を「300ms 空けて 1 回再試行 → それでも
//   ダメなら 1 回だけ reload(無限リロード禁止)」で包む。B3: PageLoader は 10 秒で再読み込みボタンを出す。
//   下の各 lazy(() => import('...')) はこの shadow 経由になり、行そのものは一切変えない(=包み直しのみ)。
import { retryImport, PageLoader } from './lib/lazyWithRetry'
const lazy = (factory) => reactLazy(() => retryImport(factory))
// SPEC-GLOBAL-NO-HORIZONTAL-OVERFLOW-01 (D-207 R2): 開発時のはみ出し検出のみ(横スクロールリセットは空振りゆえ削除=S44)。
import { DevOverflowDetector } from './shared/ui/OverflowGuards'

// ===== ドメイン別ルーティング =====
// patrol.clawops.app → 巡回アプリ専用（/admin へのアクセスは admin.clawops.app にリダイレクト）
// admin.clawops.app  → 管理アプリ専用（/ へのアクセスは /admin にリダイレクト）
// ※ Step 5（clawops-tau.vercel.app → 新ドメインへの旧URLリダイレクト）はドメイン取得後に追加
const _host = window.location.hostname
if (_host === 'patrol.clawops.app' && window.location.pathname.startsWith('/admin')) {
  window.location.replace('https://admin.clawops.app' + window.location.pathname + window.location.search)
} else if (_host === 'admin.clawops.app' && !window.location.pathname.startsWith('/admin') && window.location.pathname !== '/login') {
  window.location.replace('/admin' + window.location.search)
}
import ErrorBoundary from './components/ErrorBoundary'
import { AuthProvider } from './lib/auth/AuthProvider'
import { useAuth } from './hooks/useAuth'
import ProtectedRoute, { AdminRoute, ManagerRoute, CollectionRoute, StaffReportRoute } from './components/ProtectedRoute'
import { RoleGuard } from './shared/auth/RoleGuard'
// SPEC-LF1-STORE-LOCAL-CACHE-01: 未送信件数の app-wide banner
import UnsentBanner from './components/UnsentBanner'
import OfflineContinuationBadge from './components/OfflineContinuationBadge'
// D-1085 案 1: どの画面の「?」からも開ける 不具合・要望 のシート(App に 1 つだけ)。
import FeedbackHost from './components/FeedbackHost'
// SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268i) PHASE_D: 集金未送信の app-wide banner(巡回バナーと並べる)
import CollectionUnsentBanner from './components/CollectionUnsentBanner'
import TestBuildWatermark from './components/TestBuildWatermark'
import { makeDebouncedUploadAll } from './services/storeSync'
// SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268e) B7 + FIX_B7_AMEND + B13: 集金ドラフトの送信発火 + 7日超の掃除。
import { flushCollectionDrafts } from './services/collectionSync'
import { cleanupSyncedDrafts } from './lib/localStore/collectionDrafts'
import { buildLabel } from './lib/buildInfo'
// ★D-606: build 時刻は version.json から実行時に読む(定数にすると全チャンクのハッシュが連鎖する)。
import { useBuildTime } from './hooks/useBuildTime'
import { useSessionLock } from './hooks/useIdleLogout'
// D-1046: 全ルートの画面利用計測(閲覧・滞在秒・巡回の段取り時間)。ルートの層で 1 か所だけ。
import { useRouteUsageTracking } from './hooks/useRouteUsageTracking'

// ===== 遅延読み込み =====
// 初回ロードは Login + Launcher のみ。他は画面遷移時にロード。

// 即時ロード（初回表示に必要）
import Login from './pages/Login'
import Launcher from './pages/Launcher'

// 遅延ロード — メインタブ
const Dashboard = lazy(() => import('./manesupport/pages/Dashboard'))
const DashboardTop = lazy(() => import('./dashboard/pages/DashboardTop'))
// J-ADMIN-02: Admin IA layout + hub pages (旧実装は _legacy/ に移動)
const AdminLayout           = lazy(() => import('./admin/AdminLayout'))
const AdminMastersHubPage   = lazy(() => import('./admin/pages/AdminMastersHubPage'))
const AdminPrizeAbbrevRulesPage = lazy(() => import('./admin/pages/AdminPrizeAbbrevRulesPage')) // D-1045
// SPEC-MANESUPPORT-ROLE-TABS-01 (D-140) R2: 管理タブのハブ
const AdminManageHubPage    = lazy(() => import('./admin/pages/AdminManageHubPage'))
const PrizeLinkPage         = lazy(() => import('./admin/pages/PrizeLinkPage'))
const AdminCollectionFlagPage = lazy(() => import('./admin/pages/AdminCollectionFlagPage'))
const AdminCollectionHubPage  = lazy(() => import('./admin/pages/AdminCollectionHubPage'))
const CollectionInputPage   = lazy(() => import('./collection/CollectionInputPage'))
// SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 総額一括計数。★通常入力と同じガード(CollectionRoute)。
const CollectionInputBulkPage = lazy(() => import('./collection/CollectionInputBulkPage'))
const CollectionHistoryPage = lazy(() => import('./collection/CollectionHistoryPage'))
const CashReconcilePage     = lazy(() => import('./collection/CashReconcilePage'))
const AdminDevAssetsListPage = lazy(() => import('./admin/pages/AdminDevAssetsListPage'))
const AdminDevAssetsUploadPage = lazy(() => import('./admin/pages/AdminDevAssetsUploadPage'))
const AdminAuditHubPage     = lazy(() => import('./admin/pages/AdminAuditHubPage'))
const AdminActivityLogPage  = lazy(() => import('./admin/pages/AdminActivityLogPage')) // SPEC-ACTIVITY-LOG-01 (D-302)
const AdminReportsHubPage   = lazy(() => import('./admin/pages/AdminReportsHubPage'))
// SPEC-STORE-DAILY-REPORT-MODULE-01 (D-866): 店舗タブ(日報・月報)。直営2店(KRM01/IZK01)向け。
const StoreHubPage                  = lazy(() => import('./storereport/StoreHubPage'))
const StoreDailyReportListPage      = lazy(() => import('./storereport/StoreDailyReportListPage'))
const StoreDailyReportFormPage      = lazy(() => import('./storereport/StoreDailyReportFormPage'))
const StoreMonthlyReportListPage    = lazy(() => import('./storereport/StoreMonthlyReportListPage'))
const StoreMonthlyReportFormPage    = lazy(() => import('./storereport/StoreMonthlyReportFormPage'))
// SPEC-STORE-SHIFT-GRID-01 (D-891): 店舗タブのシフト表(Excel シフト表久留米.xlsx の再現)。
const StoreShiftsPage               = lazy(() => import('./storereport/StoreShiftsPage'))
const StoreTaskListPage             = lazy(() => import('./storereport/StoreTaskListPage')) // D-996
const PaidLeaveListPage             = lazy(() => import('./storereport/PaidLeaveListPage')) // D-1027
const PaidLeaveDetailPage           = lazy(() => import('./storereport/PaidLeaveDetailPage')) // D-1027
const StoreRosterPage               = lazy(() => import('./storereport/StoreRosterPage')) // D-1029
const StoreLaborCostPage            = lazy(() => import('./storereport/StoreLaborCostPage')) // D-1112
const StoreShiftRequestPage         = lazy(() => import('./storereport/StoreShiftRequestPage')) // D-1113
// J-REPORTS-ANALYTICS-01 2026-05-30: 7 売上分析画面 lazy import
const BoothRankingPage    = lazy(() => import('./admin/pages/reports/BoothRankingPage'))
const PayoutTrendPage     = lazy(() => import('./admin/pages/reports/PayoutTrendPage'))
const SevenDmaPage        = lazy(() => import('./admin/pages/reports/SevenDmaPage'))
const PrizePayoutAnalysisPage = lazy(() => import('./admin/pages/reports/PrizePayoutAnalysisPage')) // D-1072
const PrizeCostPage       = lazy(() => import('./admin/pages/reports/PrizeCostPage'))
const StoreComparisonPage = lazy(() => import('./admin/pages/reports/StoreComparisonPage'))
const ProfitCalendarPage      = lazy(() => import('./admin/pages/reports/ProfitCalendarPage'))
const CollectionExportPage    = lazy(() => import('./admin/pages/reports/CollectionExportPage'))
const CollectionPdfBulkPage   = lazy(() => import('./admin/pages/reports/CollectionPdfBulkPage')) // D-453 集金PDF一括
const MonthlyTargetPage       = lazy(() => import('./admin/pages/reports/MonthlyTargetPage')) // SPEC-ADMIN-MONTHLY-TARGET-UI-01 (D-205b)
const DailySalesPayoutPage    = lazy(() => import('./admin/pages/reports/DailySalesPayoutPage')) // SPEC-DAILY-SALES-PAYOUT-LIST-V1 (D-528)
const MachineInventoryPage    = lazy(() => import('./admin/pages/reports/MachineInventoryPage')) // SPEC-MACHINE-INVENTORY-V1 (D-904) 機械内在庫一覧
const PerDiemPage             = lazy(() => import('./admin/pages/reports/PerDiemPage')) // SPEC-PER-DIEM-AUTO-01 (D-989) 日当(出張)
const ModelMonthlyAveragePage = lazy(() => import('./admin/pages/reports/ModelMonthlyAveragePage')) // SPEC-MODEL-MONTHLY-AVERAGE-01 (D-926) 機種別 月平均売上
const NicelandSlipPage        = lazy(() => import('./admin/pages/reports/NicelandSlipPage')) // D-992 ナイスランド宛 集金伝票
const AdminReleaseNotesPage  = lazy(() => import('./admin/pages/AdminReleaseNotesPage')) // D-1092 機能改善履歴
const OpsHealthRadarPage      = lazy(() => import('./admin/pages/OpsHealthRadarPage')) // SPEC-OPS-HEALTH-RADAR-V1 (D-574) 経営レーダー(6軸)
const OrderAckAuditPage       = lazy(() => import('./admin/pages/OrderAckAuditPage')) // SPEC-ORDER-DOC-TEXT-EXTRACT-V1 (D-575) 請書の取込状況
const AdminSettingsHubPage  = lazy(() => import('./admin/pages/AdminSettingsHubPage'))
const AdminAlertSettingsPage = lazy(() => import('./admin/pages/AdminAlertSettingsPage')) // D-1121
const AdminPlaceholderPage  = lazy(() => import('./admin/pages/AdminPlaceholderPage'))
const AdminMachineLayoutPage   = lazy(() => import('./admin/pages/AdminMachineLayoutPage'))
const AdminStaffListPage     = lazy(() => import('./admin/pages/AdminStaffListPage'))
const AdminSupplierPage           = lazy(() => import('./admin/pages/AdminSupplierPage'))
const AdminOperationLogsPage      = lazy(() => import('./admin/pages/AdminOperationLogsPage'))
const AdminLoginLogsPage          = lazy(() => import('./admin/pages/AdminLoginLogsPage'))
const AdminPrizePhaseHistoryPage  = lazy(() => import('./admin/pages/AdminPrizePhaseHistoryPage'))
const AdminStockMovementsPage     = lazy(() => import('./admin/pages/AdminStockMovementsPage'))
// SPEC-STORE-ROUND-HISTORY-LIST-01 (D-818): 店舗ラウンド履歴一覧(監査・履歴)。
const StoreRoundHistoryPage       = lazy(() => import('./admin/pages/StoreRoundHistoryPage'))
const AdminStoreListPage          = lazy(() => import('./admin/pages/AdminStoreListPage'))
const AdminBulkImportPage         = lazy(() => import('./admin/pages/AdminBulkImportPage'))
const AdminImportHubPage          = lazy(() => import('./admin/pages/AdminImportHubPage'))

// 遅延ロード — マスタ追加
// J-NAV-ORPHAN-CLEANUP-01 2026-05-30: BoothQrPrint は /admin/qr-print の動線無し orphan のため lazy import 削除。
// D-1034: /admin/labels (AdminQRLabelPage) も運用で使っていないので撤去。BoothQrPrint.jsx 本体も削除。
const AdminModelList = lazy(() => import('./manesupport/pages/ModelList'))
const AdminMachineList = lazy(() => import('./manesupport/pages/MachineList'))
const AdminMachineModelLink = lazy(() => import('./manesupport/pages/MachineModelLinkPage'))
const AdminBoothList = lazy(() => import('./manesupport/pages/BoothList'))
const ManualEditor = lazy(() => import('./manesupport/pages/ManualEditor'))
const ManualView = lazy(() => import('./manesupport/pages/ManualView'))

// 遅延ロード — 管理系
const EditReading = lazy(() => import('./manesupport/pages/EditReading'))
const MeterAnomalyList = lazy(() => import('./manesupport/pages/MeterAnomalyList')) // SPEC-METER-ANOMALY-REVIEW-01 (D-292)
const DataSearch = lazy(() => import('./manesupport/pages/DataSearch'))
const LockerList = lazy(() => import('./manesupport/pages/LockerList'))
const TipsList = lazy(() => import('./manesupport/pages/TipsList')) // SPEC-UPDATE-TIPS-V1 (D-661)
// SPEC-COLLECTION-SCHEDULE-GRID-01 (D-679): 集金予定表(マネサポ)。
const CollectionSchedulePage = lazy(() => import('./manesupport/pages/CollectionSchedulePage'))
// SPEC-PATROL-SCHEDULE-BY-STORE-01 (D-710): 巡回予定表(店舗 x 日付)。集金予定表と同じ形。
//   ★D-706 の個人スケジュール(担当者 x 日付)を作り直したもの。
const PatrolSchedulePage = lazy(() => import('./manesupport/pages/PatrolSchedulePage'))
// D-710 三: 同じ表を担当者別に見るだけの画面(★管理者限定・読むだけ)。
const PatrolScheduleByStaffPage = lazy(() => import('./manesupport/pages/PatrolScheduleByStaffPage'))
// SPEC-PRIZE-PROVISIONAL-REGISTER-V1 (D-785): 仮登録の景品を確かめて正式にする(manager 以上)。
const ProvisionalPrizeList = lazy(() => import('./manesupport/pages/ProvisionalPrizeList'))
// SPEC-OPS-STATS-SELF-RECOVERY-V1 (D-790) 三: 運用の見張り(manager 以上・見るだけ)。
const OpsWatchPage = lazy(() => import('./manesupport/pages/OpsWatchPage'))
// SPEC-IN-APP-FEEDBACK-01 (D-852) AC5 follow-up: 現場からの 不具合・要望 一覧(manager 以上)。
const AppFeedbackListPage = lazy(() => import('./manesupport/pages/AppFeedbackListPage'))
const AssetBands = lazy(() => import('./manesupport/pages/AssetBands')) // SPEC-ASSET-TAG-REGISTER-01 (D-166)
const AssetInventory = lazy(() => import('./manesupport/pages/AssetInventory')) // SPEC-ASSET-INVENTORY-03 (D-170)
const ForecastList = lazy(() => import('./manesupport/pages/ForecastList'))
const MachineRankingPage = lazy(() => import('./manesupport/pages/MachineRankingPage')) // SPEC-MACHINE-RANKING-V1 (D-489)
const PrizeRankingPage   = lazy(() => import('./manesupport/pages/PrizeRankingPage')) // SPEC-PRIZE-DAILY-RANKING-V1 (D-569)
const ForecastDetail = lazy(() => import('./manesupport/pages/ForecastDetail'))
const TargetDraftPage = lazy(() => import('./manesupport/pages/TargetDraftPage')) // SPEC-MONTHLY-TARGET-DRAFT-BOOTH-MEDIAN-V1 (D-795)
const ImportSlips = lazy(() => import('./manesupport/pages/ImportSlips'))
const SetupSheets = lazy(() => import('./manesupport/pages/SetupSheets'))
const TestDataImport = lazy(() => import('./manesupport/pages/TestDataImport'))
const AuditLog = lazy(() => import('./manesupport/pages/AuditLog'))
// J-NAV-ORPHAN-CLEANUP-01 2026-05-30: AuditSummary / DailyStatsAdmin は動線無し orphan、lazy import 削除。
// SPEC-DAILY-STATS-RECOMPUTE-V1 (D-530): DailyStatsAdmin を復活させる。
//   ★2026-05-30 に動線が無いという理由で外されたが、本 D で「手入力の後に自分でボタンを押す」という
//     使い道ができた(ひろ判断)。動線ごと戻さないと押せる画面にならないので、ルートと監査ハブのタイルを付ける。
const DailyStatsAdmin       = lazy(() => import('./manesupport/pages/DailyStatsAdmin'))

// 遅延ロード — 用語マスタ管理
const AdminGlossary = lazy(() => import('./manesupport/pages/AdminGlossary'))

// 遅延ロード — タナサポ
const TanasupportHub       = lazy(() => import('./tanasupport/pages/TanasupportHub'))
const OrderList            = lazy(() => import('./tanasupport/pages/OrderList'))
const StoreDashboard       = lazy(() => import('./tanasupport/StoreDashboard'))
const StocktakeInput       = lazy(() => import('./tanasupport/stocktake/StocktakeInput'))
const StocktakeSessionPage = lazy(() => import('./tanasupport/stocktake/StocktakeSessionPage'))
const StaffStockCount      = lazy(() => import('./tanasupport/stocktake/StaffStockCount')) // D-446 車在庫カウント
const StocktakeTargetPage  = lazy(() => import('./tanasupport/pages/StocktakeTargetPage'))
const LocationHubPage      = lazy(() => import('./tanasupport/pages/LocationHubPage'))
const OcrCountTestPage     = lazy(() => import('./tanasupport/pages/OcrCountTestPage'))

// 遅延ロード — タナサポ 棚卸し管理 (マネサポ側)
const StocktakeSessionListAdmin = lazy(() => import('./manesupport/admin/stocktake/SessionListAdmin'))
const StocktakeSessionCreate    = lazy(() => import('./manesupport/admin/stocktake/SessionCreate'))
const StocktakeSessionDetail    = lazy(() => import('./manesupport/admin/stocktake/SessionDetail'))
const StocktakeDashboard        = lazy(() => import('./manesupport/admin/stocktake/StocktakeDashboard'))
const StocktakeSessionSummary   = lazy(() => import('./manesupport/admin/stocktake/SessionSummary')) // D-1003 拠点別・担当別の集計

// 遅延ロード — ヘルプ
const HelpPage = lazy(() => import('./pages/HelpPage'))
// SPEC-HELP-FIRST-PATROL-AND-SCREEN-HELP-01 (D-151) ITEM_1: 初めての巡回ページ
const FirstPatrolPage = lazy(() => import('./pages/FirstPatrolPage'))
// D-993: Face ID でログインの ON/OFF(この端末だけ)
const FaceIdSettingsPage = lazy(() => import('./pages/FaceIdSettingsPage'))

// SPEC-STAFF-INVITE-S3-TOKEN-RECEIVE-01: 招待受信画面 (ProtectedRoute外、未認証可)
const InvitePage = lazy(() => import('./pages/InvitePage'))

// 遅延ロード — J-ADMIN-01 管理者ブース編集
const AdminStorePage      = lazy(() => import('./admin/pages/AdminStorePage'))
const AdminMachineListPage = lazy(() => import('./admin/pages/AdminMachineListPage'))
const AdminBoothEditPage   = lazy(() => import('./admin/pages/AdminBoothEditPage'))
const AdminCollectionEditPage = lazy(() => import('./admin/pages/AdminCollectionEditPage'))
const AdminCollectionBoothEditPage = lazy(() => import('./admin/pages/AdminCollectionBoothEditPage'))

// 遅延ロード — J-ADMIN-05 景品マスタ + 発注履歴
const AdminPrizeMasterPage  = lazy(() => import('./admin/pages/AdminPrizeMasterPage'))
const AdminOrderHistoryPage = lazy(() => import('./admin/pages/AdminOrderHistoryPage'))

// 遅延ロード — J-PATROL-ALERTS-HUB-01
const AlertListPage       = lazy(() => import('./clawsupport/pages/AlertListPage'))
const ChangerInputPage    = lazy(() => import('./clawsupport/pages/ChangerInputPage'))
const AdminAlertTypesPage = lazy(() => import('./admin/pages/AdminAlertTypesPage'))

// 遅延ロード — クレサポ v1.0 ハブ
const ClawsupportHub        = lazy(() => import('./clawsupport/pages/ClawsupportHub'))
const PatrolRoutePage       = lazy(() => import('./clawsupport/pages/PatrolRoutePage'))
const CollectionPrepPage    = lazy(() => import('./collection/CollectionPrepPage'))
const ClawsupportStoreDash  = lazy(() => import('./clawsupport/pages/ClawsupportStoreDash'))
const PatrolScreenV1        = lazy(() => import('./clawsupport/pages/PatrolScreenV1'))
// M1 Stage 2: ブース入力
const PatrolBoothInputPage  = lazy(() => import('./clawsupport/pages/PatrolBoothInputPage'))
// M1 Stage 3: 店舗ハブ (sticky summary bar + diff chips)
const PatrolStorePage       = lazy(() => import('./clawsupport/pages/PatrolStorePage'))

// 遅延ロード — OCR
// SPEC-OCR-SECOND-ENGINE-KILL-01 (D-287): PatrolCameraPage / PatrolBatchOcrPage(meter-ocr 経路B)を削除。OCRTestPage は ocr-meter 経路で維持。
const OCRTestPage = lazy(() => import('./clawsupport/pages/OCRTestPage'))

// 遅延ロード — 棚卸しアプリ（PIN認証）
// D-299 IMPL_4: 旧棚卸し未認証ルート削除に伴い StocktakeLogin/Top/Count/Summary の lazy import も撤去(ページ本体は残置=別D)。
const StockDashboard = lazy(() => import('./tanasupport/pages/StockDashboard'))
const StockMove    = lazy(() => import('./tanasupport/pages/StockMove'))
const StockCount   = lazy(() => import('./tanasupport/pages/StockCount'))
const StockOutPage       = lazy(() => import('./tanasupport/pages/StockOutPage'))
const ArrivalCheckPage   = lazy(() => import('./tanasupport/pages/ArrivalCheckPage'))
const ArrivalPendingPage = lazy(() => import('./tanasupport/pages/ArrivalPendingPage')) // SPEC-ARRIVAL-PENDING-LIST-01 (D-319)
const StaffArrivalPage   = lazy(() => import('./tanasupport/pages/StaffArrivalPage'))   // SPEC-STAFF-ARRIVAL-ONE-TAP-WITH-PHOTO-01 (D-834)
const StockHubPage       = lazy(() => import('./tanasupport/pages/StockHubPage'))
// SPEC-PRIZE-THEORETICAL-STOCK-PAGE-01 (D-404): 機械内景品の理論在庫ページ
const PrizeTheoreticalStockPage = lazy(() => import('./tanasupport/pages/PrizeTheoreticalStockPage'))
// SPEC-STOCK-ANNOUNCEMENTS-01: 景品案内ビューア (お気に入り付き)
const AnnouncementsPage  = lazy(() => import('./pages/stock/AnnouncementsPage'))


// ローディングスピナー（Suspense フォールバック）は D-230 で lib/lazyWithRetry の PageLoader へ移設。
//   10 秒以上決着しない場合に再読み込みボタンを出す(B3)。ここでは import 済のものを使う。

// SPEC-AUTH-TIMEOUT-LOCKSCREEN-01: hidden 中の視覚カバー。position:fixed + inset-0 で viewport
// 端固定 (既知の iOS h-dvh/h-svh cutoff を避けるため height-class は使わない)。Routes は下で
// マウントされ続けるので、復帰(isLocked→false)時は離れた画面へ remount/reload なしで即戻る。
// 既存 design token のみ (--color-bg / --color-accent / --color-muted)。読み込みではないので spinner なし。
function AppLockOverlay() {
  return (
    <div className="fixed inset-0 z-[200] bg-bg flex items-center justify-center">
      <div className="text-center">
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor"
             strokeWidth="1.8" className="text-accent mx-auto mb-3" aria-hidden="true">
          <rect x="4" y="10" width="16" height="10" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
        <p className="text-muted text-sm">ロック中</p>
      </div>
    </div>
  )
}

// DIAG-SWIPE-BLACKSCREEN-01 fix A: useParams から boothCode を読み、key prop に渡すことで
// React Router v6 の param-only update では起きないコンポーネント再マウントを強制する。
// これにより PatrolBoothInputPage の useState 初期化 (consumePendingEnterFrom) と
// 入場アニメ useEffect[] が boothCode 変更毎に再実行され、ANIM-01 の swipeDx が初期化される。
function PatrolBoothInputPageKeyed() {
  const { boothCode } = useParams()
  return <PatrolBoothInputPage key={boothCode} />
}

// D-1046: 描画は何もしない。Router の中・Routes の外で 1 回だけ置く(各画面には手を入れない)。
function RouteUsageTracker() {
  useRouteUsageTracking()
  return null
}

function AppInner() {
  const { isLoggedIn, staffId, staffName } = useAuth()
  // SPEC-AUTH-TIMEOUT-LOCKSCREEN-01: hidden 中は isLocked=true。ログイン中のみカバー表示。
  const isLocked = useSessionLock(isLoggedIn)
  // ★D-606: 右下の build ラベルに出す時刻。取れるまでは null で、ラベルは時刻抜きで出る。
  const buildTime = useBuildTime()

  const initGlossary = useGlossaryStore(s => s.init)
  const cleanupGlossary = useGlossaryStore(s => s.cleanup)

  // D-299 IMPL_3(方針C): 用語は認証後に取得する。従来 useEffect([]) は未認証(anon)で走り、glossary_terms が
  //   読めず黙って空のままだった(長押し解説が無反応)。isLoggedIn の変化で再 init(init 側で channel 二重購読回避)。
  useEffect(() => {
    initGlossary()
    return () => cleanupGlossary()
  }, [isLoggedIn]) // eslint-disable-line react-hooks/exhaustive-deps

  // SPEC-LF1-IDEMPOTENT-SYNC-01 D6: iOS tab-kill が unmount trigger を飛ばす対策。
  // 'online' 復帰 + visibilitychange(visible) で shared 10s debounce → uploadAllUnsynced を
  // fire-and-forget。ClawsupportHub / PatrolStorePage 既存 trigger はそのまま残す (追加のみ)。
  useEffect(() => {
    if (!isLoggedIn || typeof window === 'undefined') return
    const trigger = makeDebouncedUploadAll({ getStaff: () => ({ staffId }) })
    const onVisible = () => { if (document.visibilityState === 'visible') trigger() }
    window.addEventListener('online', trigger)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('online', trigger)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [isLoggedIn, staffId])

  // SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268e) B7 + FIX_B7_AMEND: 集金ドラフトの送信発火。
  //   アプリ起動(ログイン確立)+ online + visibilitychange(visible)の3点のみ(iOS は Background Sync 無し・online 取りこぼしあり)。
  //   送信は経路1本(collectionSync.sendDraft)を通り、冪等キーで二重にならない。B13: 起動時に7日超の送信済みドラフトを掃除。
  useEffect(() => {
    if (!isLoggedIn || typeof window === 'undefined') return
    let t = null
    const flush = () => { flushCollectionDrafts({ staff: { staffId, staffName } }).catch(() => {}) }
    const debounced = () => { if (t) clearTimeout(t); t = setTimeout(() => { t = null; flush() }, 3000) }
    const onVisible = () => { if (document.visibilityState === 'visible') debounced() }
    cleanupSyncedDrafts().catch(() => {}) // B13: 送信済み7日超を Blob ごと掃除(未送信は絶対に消さない)
    flush()                               // 起動時に一度、溜まった未送信を掃き出す(B7)
    window.addEventListener('online', debounced)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      if (t) clearTimeout(t)
      window.removeEventListener('online', debounced)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [isLoggedIn, staffId, staffName])
  return (
    <ErrorBoundary>
      {/* SPEC-GLOBAL-NO-HORIZONTAL-OVERFLOW-01 (D-207 R2) S41: はみ出し検出は import.meta.env.DEV のみ mount(本番は非mount)。
          伝染は S43(min-width:0)で不要な横あふれ自体を消して止める。横スクロールリセットは空振りゆえ削除(S44)。 */}
      {import.meta.env.DEV && <DevOverflowDetector />}
      {/* SPEC-TESTBADGE-WATERMARK-ALLPAGES-01 (D-114): テスト版透かし。ログイン前画面(/login,/invite)含む全ページで
          識別したいので isLoggedIn 条件を付けず無条件mount。本番(__VERCEL_ENV__=production)では自動で null。 */}
      <TestBuildWatermark />
      {/* SPEC-AUTH-TIMEOUT-LOCKSCREEN-01: hidden 中の視覚カバー。Routes を unmount せず被せるだけ。
          ツリー先頭に置き、復帰時に最速で描画されるようにする。 */}
      {isLoggedIn && isLocked && <AppLockOverlay />}
      {/* D-304: 圏外継続中バッジ(offline はバッジ側で見る)。梯子 top-40 で未送信/容量バナーと重ねない。 */}
      {isLoggedIn && <OfflineContinuationBadge />}
      {/* D-1085 案 1: 「?」の一番下の「この画面の不具合・要望を送る」/ openFeedback() で開く。Router の中・Routes の外。 */}
      {isLoggedIn && <FeedbackHost />}
      {/* SPEC-LF1-STORE-LOCAL-CACHE-01: app-wide 未送信件数バナー */}
      {isLoggedIn && <UnsentBanner />}
      {/* SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268i) PHASE_D D1/FIX_D7: 集金未送信バナー(常時・自分/他人分割) */}
      {isLoggedIn && <CollectionUnsentBanner />}
      {isLoggedIn && (
        <div className="fixed bottom-1 right-1 z-[90] text-[8px] text-muted/20 pointer-events-none select-none">
          {buildLabel(buildTime)}
        </div>
      )}
      <RouteUsageTracker />
      <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/login" element={<Login />} />
      {/* SPEC-STAFF-INVITE-S3-TOKEN-RECEIVE-01: 招待受信画面 (ProtectedRoute外、未認証可) */}
      <Route path="/invite" element={<InvitePage />} />

      {/* ホーム = ランチャー（ロール別タイル表示） */}
      <Route path="/launcher" element={<ProtectedRoute><Launcher /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><Navigate to="/launcher" replace /></ProtectedRoute>} />
      {/* SPEC-UI-B-DECOMM-LEGACY-PATROL-01: 旧 /input (MainInput) 削除 → Navigate catch */}
      <Route path="/input" element={<Navigate to="/clawsupport" replace />} />
      {/* J-COLLECTION-01: 集金 / J-COLLECTION-12 R1: admin/manager のみ。
          staff/patrol で直接 URL 入力時は ManagerRoute (RoleRoute) が '/' へ redirect → /launcher。 */}
      {/* D-312 S4b: 集金3画面を CollectionRoute(admin/manager/contractor)へ。他の ManagerRoute は不変。/collection/prep は ProtectedRoute のまま。 */}
      <Route path="/collection/input" element={<CollectionRoute><CollectionInputPage /></CollectionRoute>} />
      {/* D-689: 一括集金。来店のたびにどちらの数え方かを現場が選ぶ(店舗マスタでは判定しない)。 */}
      <Route path="/collection/input-bulk" element={<CollectionRoute><CollectionInputBulkPage /></CollectionRoute>} />
      <Route path="/collection/history" element={<CollectionRoute><CollectionHistoryPage /></CollectionRoute>} />
      <Route path="/collection/reconciliation" element={<CollectionRoute><CashReconcilePage /></CollectionRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardTop /></ProtectedRoute>} />
      {/* J-NAV-ORPHAN-CLEANUP-01 2026-05-30: /dashboard/legacy ルート削除 (動線無し) */}
      {/* J-ADMIN-02: AdminLayout nested routes (新 IA ナビ骨組) */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="masters" replace />} />
        {/* SPEC-MANESUPPORT-ROLE-TABS-01 (D-140) R2: 管理タブ。ルート可視性は AdminLayout の R4 ガードで担保。 */}
        <Route path="manage" element={<AdminManageHubPage />} />
        <Route path="masters" element={<AdminMastersHubPage />} />
        <Route path="masters/store-list" element={<AdminStoreListPage />} />
        <Route path="masters/stores" element={<AdminStorePage />} />
        <Route path="masters/prizes" element={<AdminPrizeMasterPage />} />
        <Route path="masters/staff" element={<AdminStaffListPage />} />
        <Route path="masters/suppliers" element={<AdminSupplierPage />} />
        {/* D-1045: 景品の短縮ルール。manager 以上(タブ判定は roles.js の例外で manage に寄せる)。 */}
        <Route path="masters/abbrev-rules" element={<ManagerRoute><AdminPrizeAbbrevRulesPage /></ManagerRoute>} />
        <Route path="masters/*" element={<AdminPlaceholderPage />} />
        <Route path="audit" element={<AdminAuditHubPage />} />
        {/* SPEC-ACTIVITY-LOG-01 (D-302): 活動ログ(読み取り専用)。AdminLayout 配下。 */}
        <Route path="activity" element={<AdminActivityLogPage />} />
        <Route path="audit/orders" element={<AdminOrderHistoryPage />} />
        <Route path="audit/booth-edit" element={<AdminMachineListPage />} />
        <Route path="audit/booth-edit/:storeCode/machines" element={<AdminMachineListPage />} />
        <Route path="audit/booth-edit/:boothCode" element={<AdminBoothEditPage />} />
        <Route path="audit/collection-edit" element={<AdminCollectionEditPage />} />
        <Route path="audit/collection-edit/c/:collectionId" element={<AdminCollectionBoothEditPage />} />
        <Route path="audit/operations"  element={<AdminOperationLogsPage />} />
        <Route path="audit/logins"      element={<AdminLoginLogsPage />} />
        <Route path="audit/prize-phases" element={<AdminPrizePhaseHistoryPage />} />
        <Route path="audit/stock-moves" element={<AdminStockMovementsPage />} />
        <Route path="audit/bulk-import" element={<AdminBulkImportPage />} />
        <Route path="audit/daily-stats" element={<DailyStatsAdmin />} />{/* D-530 */}
        <Route path="audit/store-rounds" element={<StoreRoundHistoryPage />} />{/* D-818 */}
        <Route path="audit/*" element={<AdminPlaceholderPage />} />
        <Route path="reports" element={<AdminReportsHubPage />} />
        {/* J-REPORTS-ANALYTICS-01 2026-05-30: 7 売上分析画面 */}
        <Route path="reports/booth-ranking"    element={<BoothRankingPage />} />
        <Route path="reports/payout-trend"     element={<PayoutTrendPage />} />
        <Route path="reports/7dma"             element={<SevenDmaPage />} />
        <Route path="reports/prize-cost"       element={<PrizeCostPage />} />
        <Route path="reports/store-comparison" element={<StoreComparisonPage />} />
        <Route path="reports/profit-calendar"  element={<ProfitCalendarPage />} />
        <Route path="reports/collections"       element={<CollectionExportPage />} />
        <Route path="reports/collection-pdf-bulk" element={<CollectionPdfBulkPage />} />{/* D-453 */}
        <Route path="reports/monthly-target"    element={<MonthlyTargetPage />} />
        <Route path="reports/daily-sales-payout" element={<DailySalesPayoutPage />} />{/* D-528 */}
        <Route path="reports/model-monthly-average" element={<ModelMonthlyAveragePage />} />{/* D-926 */}
        {/* D-1072: 景品別 適正出率。admin / manager だけ(patrol は分析タブが見えるのでルートでも絞る)。 */}
        <Route path="reports/prize-payout" element={<ManagerRoute><PrizePayoutAnalysisPage /></ManagerRoute>} />
        {/* D-992: admin だけ。AdminLayout(分析タブ)の内側に置き、さらに AdminRoute で admin に絞る(manager は分析タブが見えるため)。 */}
        <Route path="reports/niceland-slip" element={<AdminRoute><NicelandSlipPage /></AdminRoute>} />
        <Route path="reports/*" element={<AdminPlaceholderPage />} />
        {/* SPEC-STORE-DAILY-REPORT-MODULE-01 (D-866): 店舗タブ(日報・月報)。RLSは current_staff_direct_stores()
            で絞る(AdminLayout の R4 ガードは roles.js の 'store' タブで担保、この配下はさらに RPC/RLS 側でも
            店を検査する=UI 側の絞り込みだけに頼らない)。 */}
        <Route path="store" element={<StoreHubPage />} />
        <Route path="store/:storeCode/daily" element={<StoreDailyReportListPage />} />
        <Route path="store/:storeCode/daily/:date" element={<StoreDailyReportFormPage />} />
        <Route path="store/:storeCode/monthly" element={<StoreMonthlyReportListPage />} />
        <Route path="store/:storeCode/monthly/:yearMonth" element={<StoreMonthlyReportFormPage />} />
        {/* SPEC-STORE-SHIFT-GRID-01 (D-891): シフト表。年月は暦月(storeMonthlyReport の集金日基準とは別)。 */}
        <Route path="store/:storeCode/shifts/:yearMonth" element={<StoreShiftsPage />} />
        {/* SPEC-STORE-TASK-LIST-01 (D-996): 作業リスト。店の絞りは store_todos の RLS、削除は RPC(社員以上)。 */}
        <Route path="store/:storeCode/tasks" element={<StoreTaskListPage />} />
        {/* D-1027: 有休管理。店舗タブは直営店の staff にも見えるので、ここは ManagerRoute で manager 以上に絞る
            (タイルを隠すだけにしない。DB は paid_leave_* の RLS でも manager 以上 + 直営店に絞る)。 */}
        <Route path="store/:storeCode/paid-leave" element={<ManagerRoute><PaidLeaveListPage /></ManagerRoute>} />
        <Route path="store/:storeCode/paid-leave/:employeeId" element={<ManagerRoute><PaidLeaveDetailPage /></ManagerRoute>} />
        {/* D-1029: 店舗スタッフ名簿。有休管理と同じく manager 以上だけ(タイル・ルート・DB の 3 か所)。 */}
        <Route path="store/:storeCode/roster" element={<ManagerRoute><StoreRosterPage /></ManagerRoute>} />
        {/* D-1112: 人件費(見るだけ)。時給の閲覧は店に入れる人(ひろの決定)なのでシフト表と同じく ManagerRoute にしない。時給の表は RLS で admin・manager・staff だけ。 */}
        <Route path="store/:storeCode/labor/:yearMonth" element={<StoreLaborCostPage />} />
        <Route path="store/:storeCode/requests/:yearMonth" element={<StoreShiftRequestPage />} />{/* D-1113 休み希望(店に入れる人。翌月以降だけは RLS でも) */}
        <Route path="store/*" element={<AdminPlaceholderPage />} />
        <Route path="settings" element={<AdminSettingsHubPage />} />
        <Route path="settings/alerts" element={<AdminRoute><AdminAlertSettingsPage /></AdminRoute>} />{/* D-1121 アラート設定(admin だけ) */}
        <Route path="settings/*" element={<AdminPlaceholderPage />} />
        <Route path="import" element={<AdminImportHubPage />} />
        <Route path="collection" element={<AdminCollectionHubPage />} />
        <Route path="collection-flag" element={<AdminCollectionFlagPage />} />
        {/* D-1034: QRラベル(AdminQRLabelPage)は撤去。ブックマーク/PWA 残存 URL は /admin(ロールの最初の可視タブ)へ。 */}
        <Route path="labels" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<AdminPlaceholderPage />} />
      </Route>

      {/* J-ADMIN-01 backward-compat flat routes (regression keep) */}
      <Route path="/admin/booth-edit/:boothCode" element={<ProtectedRoute><div className="h-svh"><AdminBoothEditPage /></div></ProtectedRoute>} />

      {/* SPEC-UI-B-DECOMM-LEGACY-PATROL-01: 旧巡回フロー削除。ブックマーク/PWA残存URLが404に
          ならないよう Navigate catch に置換 (canonical /clawsupport へ)。 */}
      <Route path="/booth/:machineId" element={<Navigate to="/clawsupport" replace />} />
      <Route path="/drafts" element={<Navigate to="/clawsupport" replace />} />
      <Route path="/complete" element={<Navigate to="/clawsupport" replace />} />
      <Route path="/machines/:storeId" element={<Navigate to="/clawsupport" replace />} />

      {/* J-PATROL-ALERTS-HUB-01 */}
      <Route path="/clawsupport/alerts" element={<ProtectedRoute><AlertListPage /></ProtectedRoute>} />
      <Route path="/admin/alert-types"  element={<AdminRoute><AdminAlertTypesPage /></AdminRoute>} />

      {/* J-CHANGER-01: 両替機専用入力画面 (ブース層スキップ、machine_code 直結) */}
      <Route path="/clawsupport/changer/:machineCode" element={<ProtectedRoute><ChangerInputPage /></ProtectedRoute>} />

      {/* クレサポ v1.0 — 全ロール */}
      <Route path="/clawsupport" element={<ProtectedRoute><ClawsupportHub /></ProtectedRoute>} />
      {/* SPEC-PATROL-ROUTE-BUILDER-01 (D-106): 今日の巡回ルート作成 */}
      <Route path="/clawsupport/route" element={<ProtectedRoute><PatrolRoutePage /></ProtectedRoute>} />
      {/* SPEC-PATROL-SCHEDULE-BY-STORE-01 (D-710): 巡回予定表(店舗 x 日付)。
          ★全ロール(ProtectedRoute)。現場が自分で予定を入れる画面なので閉じない。
          ★クレサポトップのボタンと同じ行き先。タイルとガードがずれると「見えるのに弾かれる」
            (D-515 / D-701 / D-707 で 3 度踏んでいる。D-708 の横断検査が見張っている)。 */}
      <Route path="/clawsupport/schedule" element={<ProtectedRoute><PatrolSchedulePage /></ProtectedRoute>} />
      {/* SPEC-CHANGER-CASH-S1-PREP-MENU-01 (D-125): 集金前準備 (集金メニューとは独立) */}
      <Route path="/collection/prep" element={<ProtectedRoute><CollectionPrepPage /></ProtectedRoute>} />
      {/* M1 Stage 3: 店舗ハブ */}
      <Route path="/clawsupport/store/:storeCode" element={<ProtectedRoute><PatrolStorePage /></ProtectedRoute>} />
      {/* DIAG-SWIPE-BLACKSCREEN-01 fix A: key={boothCode} で boothCode 変更時にコンポーネントを
          強制 remount し、ANIM-01 の swipeDx state (commit swipe 後 -innerWidth 残存) を初期化。
          これにより useState 初期化 + useEffect[] が再実行され、入場アニメ + 黒画面解消。
          useSwipeNav.js / swipeTransition.js / animation logic は不変。 */}
      <Route path="/clawsupport/booth/:boothCode"  element={<ProtectedRoute><PatrolBoothInputPageKeyed /></ProtectedRoute>} />
      {/* 旧ルート（緊急避難 + 後方互換） */}
      <Route path="/clawsupport/store/:storeCode/dash" element={<ProtectedRoute><ClawsupportStoreDash /></ProtectedRoute>} />
      {/* SPEC-PATROL-STORE-DAILY-LIST-ENTRY-V1 (D-529): D-528 の画面を店舗固定で開く。画面は複製しない。 */}
      <Route path="/clawsupport/store/:storeCode/daily" element={<ProtectedRoute><DailySalesPayoutPage /></ProtectedRoute>} />
      <Route path="/clawsupport/store/:storeCode/patrol" element={<ProtectedRoute><PatrolScreenV1 /></ProtectedRoute>} />

      {/* SPEC-UI-B-DECOMM-LEGACY-PATROL-01: 旧巡回 /patrol クラスタ削除 → Navigate catch。
          SPEC-OCR-SECOND-ENGINE-KILL-01 (D-287): OCR ツール /patrol/camera・/patrol/batch-ocr(meter-ocr 経路B)を廃止=ルート削除。
          未知パスは末尾の path="*" → /launcher に集約されるため、この2パスへの直打ちも安全に着地する。 */}
      <Route path="/patrol/overview" element={<Navigate to="/clawsupport" replace />} />
      <Route path="/patrol" element={<Navigate to="/clawsupport" replace />} />
      <Route path="/patrol/input" element={<Navigate to="/clawsupport" replace />} />
      <Route path="/patrol/booth" element={<Navigate to="/clawsupport" replace />} />

      {/* 監査ログ — manager以上 (旧ルート、/admin/audit は AdminLayout 配下に移行) */}
      {/* J-DEV-ASSET-HANDOFF-01: ファイル受け渡し (admin/manager 両方アクセス可、AdminLayout 外、ManagerRoute で staff/patrol ブロック) */}
      <Route path="/admin/dev-assets" element={<ManagerRoute><AdminDevAssetsListPage /></ManagerRoute>} />
      <Route path="/admin/dev-assets/upload" element={<ManagerRoute><AdminDevAssetsUploadPage /></ManagerRoute>} />
      {/* J-NAV-ORPHAN-CLEANUP-01 2026-05-30: /admin/audit-summary ルート削除 (動線無し orphan) */}

      {/* データ検索・修正 — manager以上 */}
      <Route path="/datasearch" element={<ManagerRoute><DataSearch /></ManagerRoute>} />
      <Route path="/edit/:boothId" element={<ManagerRoute><EditReading /></ManagerRoute>} />
      {/* SPEC-METER-ANOMALY-REVIEW-01 (D-292): 要確認メーター一覧 — manager以上 */}
      <Route path="/admin/meter-anomalies" element={<ManagerRoute><MeterAnomalyList /></ManagerRoute>} />

      {/* J-NAV-ORPHAN-CLEANUP-01 2026-05-30: /admin/qr-print ルート削除 (動線無し)。
          D-1034: /admin/labels (AdminQRLabelPage) も撤去 → AdminLayout 配下で /admin へ転送。 */}

      {/* 用語マスタ管理 — admin のみ */}
      <Route path="/admin/glossary" element={<AdminRoute><AdminGlossary /></AdminRoute>} />

      {/* タナサポ — manager以上 */}
      <Route path="/tanasupport" element={<ManagerRoute><TanasupportHub /></ManagerRoute>} />
      <Route path="/tanasupport/orders" element={<ManagerRoute><OrderList /></ManagerRoute>} />
      <Route path="/tanasupport/store/:storeCode" element={<ManagerRoute><StoreDashboard /></ManagerRoute>} />
      {/* M2 Stage 1: 倉庫ロケーション棚卸し */}
      {/* SPEC-STOCKTAKE-REACHABLE-AND-CLOSEABLE-V1 (D-436) ITEM_3: 拠点のアルバイト共有アカウントは staff ロール想定。
          倉庫実数入力を staff/patrol からも到達可能にするため ManagerRoute→ProtectedRoute へ緩和(入荷/出庫と整合)。 */}
      <Route path="/tanasupport/location/:locationId/stocktake" element={<ProtectedRoute><StocktakeInput /></ProtectedRoute>} />
      {/* M2 Stage 2: セッション詳細 (機械・個人・合計) */}
      <Route path="/tanasupport/stocktake" element={<ManagerRoute><StocktakeSessionPage /></ManagerRoute>} />
      {/* 旧 store/stocktake ルートはハブへリダイレクト (M2 Stage 1 で session 粒度変更) */}
      <Route path="/tanasupport/store/:storeCode/stocktake" element={<Navigate to="/tanasupport" replace />} />
      <Route path="/tanasupport/store/:storeCode/stocktake/:sessionId" element={<Navigate to="/tanasupport" replace />} />

      {/* 棚卸し管理 — admin のみ */}
      {/* SPEC-ADMIN-STOCKTAKE-ENTRY-V1 (D-562) ITEM_3/ITEM_4:
          ★AdminRoute(admin のみ)から ManagerRoute(admin + manager)へ。
            MODULE_ACCESS.stocktake も ROLE_VISIBLE_TABS.manage も admin/manager なので、
            admin だけに閉じているのは他の定義と食い違っていた。AC5 が manager を要求している。
            patrol / staff / contractor は ManagerRoute で従来どおり弾かれる。 */}
      <Route path="/admin/stocktake" element={<ManagerRoute><StocktakeSessionListAdmin /></ManagerRoute>} />
      <Route path="/admin/stocktake/create" element={<ManagerRoute><StocktakeSessionCreate /></ManagerRoute>} />
      <Route path="/admin/stocktake/dashboard" element={<ManagerRoute><StocktakeDashboard /></ManagerRoute>} />
      <Route path="/admin/stocktake/:sessionId" element={<ManagerRoute><StocktakeSessionDetail /></ManagerRoute>} />
      <Route path="/admin/stocktake/:sessionId/summary" element={<ManagerRoute><StocktakeSessionSummary /></ManagerRoute>} />

      {/* ヘルプ — 全認証ユーザー */}
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      {/* D-151 ITEM_1: 初めての巡回(ヘルプのチュートリアルから遷移) */}
      <Route path="/help/first-patrol" element={<ProtectedRoute><FirstPatrolPage /></ProtectedRoute>} />
      {/* D-993: Face ID でログイン設定 — 全認証ユーザー(自分の端末の ON/OFF だけ) */}
      <Route path="/settings/face-id" element={<ProtectedRoute><FaceIdSettingsPage /></ProtectedRoute>} />

      {/* マスタ管理 — admin のみ */}
      <Route path="/admin/lockers" element={<AdminRoute><LockerList /></AdminRoute>} />
      {/* SPEC-UPDATE-TIPS-V1 (D-661): 更新待ちに出す Tips の管理。書き込みは admin だけ(RLS と同じ絞り)。 */}
      <Route path="/admin/tips" element={<AdminRoute><TipsList /></AdminRoute>} />
      {/* SPEC-COLLECTION-SCHEDULE-GRID-01 (D-679): 集金予定表。マネサポなので AdminRoute。 */}
      {/* SPEC-COLLECTION-SCHEDULE-MANAGER-ACCESS-01 (D-701): admin だけ → admin + manager。
          ★D-679 のまま AdminRoute だったが、D-690 で入口タイルを集金タブに置いたことで
            manager と contractor には「タイルが見えるのにタップすると弾かれる」状態になっていた
            (実例: honda / STAFF-13 / contractor)。D-515 と同じ形の再発。
          ★contractor はここでは開けないので、タイル側でも出さない
            (AdminCollectionHubPage が同じ MANAGER_ROLES を見る)。 */}
      <Route path="/admin/collection-schedule" element={<ManagerRoute><CollectionSchedulePage /></ManagerRoute>} />
      {/* SPEC-PATROL-SCHEDULE-BY-STORE-01 (D-710) 三: 巡回予定を担当者別に見る(読むだけ)。
          ★ManagerRoute。AdminManageHubPage のタイルも同じ MANAGER_ROLES を見る(D-708)。
          ★roles.js の PREFIX_TAB にも足してある(manage タブ扱い)。足さないと AdminLayout の
            R4 を素通りして、タブが見えないロールが直打ちで入れてしまう(D-707 の b 型)。 */}
      <Route path="/admin/patrol-schedule-by-staff" element={<ManagerRoute><PatrolScheduleByStaffPage /></ManagerRoute>} />
      {/* SPEC-PRIZE-PROVISIONAL-REGISTER-V1 (D-785) 三: 仮登録の景品の検収。
          ★ManagerRoute。AdminManageHubPage のタイルも同じ MANAGER_ROLES を見る(D-708 / D-710)。
          ★phase を書き換えるので RLS の admin_update(admin / manager)と揃っている。
          ★roles.js の PREFIX_TAB にも足してある(manage タブ扱い)。足さないと
            tabKeyForAdminPath が null を返し、AdminLayout の R4 を素通りする = D-707 の b 型。 */}
      <Route path="/admin/provisional-prizes" element={<ManagerRoute><ProvisionalPrizeList /></ManagerRoute>} />
      {/* SPEC-OPS-STATS-SELF-RECOVERY-V1 (D-790) 三: 夜間処理の失敗と集計の古さを見る。
          ★ManagerRoute。AdminManageHubPage のタイルも同じ MANAGER_ROLES を見る(D-708 / D-710 / D-785)。
          ★★見るだけの画面。直すボタンは置いていない(直し方は毎回違う)。
          ★roles.js の PREFIX_TAB にも足してある(manage タブ扱い)。足さないと
            tabKeyForAdminPath が null を返し、AdminLayout の R4 を素通りする = D-707 の b 型。 */}
      <Route path="/admin/ops-watch" element={<ManagerRoute><OpsWatchPage /></ManagerRoute>} />
      {/* SPEC-IN-APP-FEEDBACK-01 (D-852) AC5 follow-up: 現場からの 不具合・要望 一覧。
          ★ManagerRoute。AdminManageHubPage のタイルも同じ MANAGER_ROLES を見る(D-708 と同じ形)。
          ★roles.js の PREFIX_TAB にも足してある(manage タブ扱い)。足さないと
            tabKeyForAdminPath が null を返し、AdminLayout の R4 を素通りする = D-707 の b 型。 */}
      <Route path="/admin/feedback" element={<ManagerRoute><AppFeedbackListPage /></ManagerRoute>} />
      {/* SPEC-MACHINE-INVENTORY-V1 (D-904): 機械内在庫一覧。★ManagerRoute。AdminManageHubPage のタイルも
          同じ MANAGER_ROLES を見る(D-708 と同じ形)。roles.js の PREFIX_TAB にも足してある(manage タブ扱い)。 */}
      <Route path="/admin/machine-inventory" element={<ManagerRoute><MachineInventoryPage /></ManagerRoute>} />
      {/* SPEC-FORECAST-MANAGER-VISIBILITY-V1 (D-515): 売上予測を manager 可へ。D-140 R5 で他の閲覧系が
          ManagerRoute へ寄せられた際に取り残された更新漏れの是正(意図的な制限ではない)。
          manager は roles.js の可視タブ表で reports タブを見られるので /admin/reports の「売上予測」タイルは
          出ていたが、このルートだけ AdminRoute のままでタップすると '/' へ弾かれていた。 */}
      <Route path="/admin/forecast" element={<ManagerRoute><ForecastList /></ManagerRoute>} />
      {/* SPEC-MACHINE-RANKING-V1 (D-489): 機械別月次ランキング(店舗単位。全店横断は v2) */}
      <Route path="/admin/machine-ranking" element={<AdminRoute><MachineRankingPage /></AdminRoute>} />
      {/* SPEC-PER-DIEM-AUTO-01 (D-989): 日当(出張)。★admin のみ(AdminRoute)。ManagerRoute にしない。入口は各履歴(admin のみのタブ)。
          ★D-989b: さらに本田広宣(STAFF-03)専用。持ち主でない admin は PerDiemPage の中で '/' へ戻す(canUsePerDiem)。 */}
      <Route path="/admin/per-diem" element={<AdminRoute><PerDiemPage /></AdminRoute>} />
      {/* D-1092: 機能改善履歴。★admin のみ(AdminRoute)。入口は各履歴ハブ(各履歴タブは admin だけが持つ)。 */}
      <Route path="/admin/release-notes" element={<AdminRoute><AdminReleaseNotesPage /></AdminRoute>} />
      {/* SPEC-PRIZE-RANKING-STAFF-ACCESS-V1 (D-600): 景品ランキングは運用/設定する人が見る参照系。
          ★admin + manager + staff。patrol / contractor は従来どおり弾く。
          ★経営レーダー(下の /admin/ops-radar)は ManagerRoute のまま(ひろ確認済み)。
          ★staff はマネサポの可視タブが 0 なので、分析ハブ経由の導線が無い(直リンクのみ)。
            導線の置き場所はひろ判断待ち(status_log 参照)。 */}
      <Route path="/admin/reports/prize-ranking" element={<StaffReportRoute><PrizeRankingPage /></StaffReportRoute>} />
      {/* SPEC-OPS-HEALTH-RADAR-V1 (D-574) 経営レーダー。
          ★ひろ「マネージャー以上で管理したい」。ManagerRoute(admin + manager)で閉じる。
            patrol / staff / contractor は従来どおり '/' へ弾かれる(FORBIDDEN/AC1)。 */}
      <Route path="/admin/ops-radar" element={<ManagerRoute><OpsHealthRadarPage /></ManagerRoute>} />
      {/* SPEC-ORDER-DOC-TEXT-EXTRACT-V1 (D-575) 請書の取込状況。
          ★発注(orders)は MODULE_ACCESS で admin/manager。ManagerRoute で閉じる。 */}
      <Route path="/admin/order-ack" element={<ManagerRoute><OrderAckAuditPage /></ManagerRoute>} />
      {/* D-515: 詳細も同じく manager 可。設定フォーム(サイクル開始日/次回集金予定日の upsert)も開くが、
          store_forecast_settings の RLS は元から admin_insert / admin_update とも
          current_staff_role() IN ('admin','manager') を許可しており、DB 側は既に manager を想定していた。 */}
      <Route path="/admin/forecast/:storeCode" element={<ManagerRoute><ForecastDetail /></ManagerRoute>} />
      {/* D-795 月次目標の叩き台。★目標を書く画面なので ManagerRoute(3 層のうち 1 層目)。 */}
      <Route path="/admin/target-draft" element={<ManagerRoute><TargetDraftPage /></ManagerRoute>} />
      <Route path="/admin/models" element={<AdminRoute><AdminModelList /></AdminRoute>} />
      <Route path="/admin/machine-models" element={<AdminRoute><AdminMachineLayoutPage /></AdminRoute>} />
      {/* SPEC-MANESUPPORT-ROLE-TABS-01 (D-140) R5: 管理タブから開くため manager 可に緩和(AdminRoute→ManagerRoute)。 */}
      <Route path="/admin/machines" element={<ManagerRoute><AdminMachineList /></ManagerRoute>} />
      {/* SPEC-MACHINE-MODEL-LINK-ADMIN-01 (D-101): 全店横断 機械一覧・model_id紐付管理 */}
      <Route path="/admin/machine-links" element={<AdminRoute><AdminMachineModelLink /></AdminRoute>} />
      {/* D-140 R5: 管理タブから開くため manager 可に緩和。 */}
      <Route path="/admin/booths" element={<ManagerRoute><AdminBoothList /></ManagerRoute>} />
      {/* SPEC-ASSET-TAG-REGISTER-01 (D-166) UI_ADMIN: 資産タグ帯管理 */}
      <Route path="/admin/asset-bands" element={<ManagerRoute><AssetBands /></ManagerRoute>} />
      {/* SPEC-ASSET-INVENTORY-03 (D-170) UI_ADMIN_ADD: 未配置在庫 */}
      <Route path="/admin/asset-inventory" element={<ManagerRoute><AssetInventory /></ManagerRoute>} />
      {/* SPEC-PRIZE-LINK-S2-UI-01 (D-124): 景品紐付一覧 (マスタハブ配下) */}
      <Route path="/admin/prize-links" element={<ManagerRoute><PrizeLinkPage /></ManagerRoute>} />
      {/* マニュアル — admin管理 + 全ロール閲覧 */}
      <Route path="/admin/manuals" element={<AdminRoute><ManualEditor /></AdminRoute>} />
      <Route path="/manual/:modelId" element={<ProtectedRoute><ManualView /></ProtectedRoute>} />

      {/* 管理ツール — admin のみ */}
      <Route path="/admin/import-slips" element={<AdminRoute><ImportSlips /></AdminRoute>} />
      <Route path="/admin/setup-sheets" element={<AdminRoute><SetupSheets /></AdminRoute>} />
      <Route path="/admin/test-data" element={<AdminRoute><TestDataImport /></AdminRoute>} />
      {/* J-NAV-ORPHAN-CLEANUP-01 2026-05-30: /admin/daily-stats ルート削除 (動線無し orphan) */}


      {/* OCRテスト(ocr-meter 経路)。D-287 で /patrol/camera・/patrol/batch-ocr(meter-ocr 経路B)は削除済。 */}
      <Route path="/ocr-test"         element={<ProtectedRoute><OCRTestPage /></ProtectedRoute>} />

      {/* J-STOCK-STORE-SELECT-01 2026-05-30 司令塔Opus spec:
          /stock に StockHubPage を載せ、staff/leader/manager/admin 全ロールアクセス可。
          旧 StocktakeLogin (PIN認証) は /stock/login へ退避 (backward-compat、必要なら復旧)。
          子ルートは ProtectedRoute (any role) に開放、staff_stores フィルタは hub 側で吸収。 */}
      {/* J-STOCK-NAVIGATION-REDESIGN-01: /stock = StocktakeTargetPage (倉庫/担当 2 タブ) に変更、
          StockHubPage (店舗選択ハブ) は履歴のため残置 (Launcher 入口は外れたため到達不能だが) */}
      <Route path="/stock" element={<ProtectedRoute><StocktakeTargetPage /></ProtectedRoute>} />
      <Route path="/stock/hub" element={<ProtectedRoute><LocationHubPage /></ProtectedRoute>} />
      {/* D-299 IMPL_4(方針E): 到達可能な未認証(ガード無し)の旧棚卸しルート /stock/login /stock/top
          /stock/count/:sessionId /stock/summary/:sessionId を削除。既に機能停止(StocktakeLogin は anon で pin 列不可、
          StocktakeTop の insert 先 stocktake_lines 不在、locations/prize_stocks も anon 0 行)。ページ本体削除は別 D。 */}
      <Route path="/stock/count" element={<ManagerRoute><StockCount /></ManagerRoute>} />

      {/* 在庫管理: J-STOCK-STORE-SELECT-01 で role ガード開放 (ProtectedRoute = any role)、
          staff の場合は staff_stores のデータのみ表示 (RLS + hub フィルタ) */}
      <Route path="/stock/dashboard" element={<ManagerRoute><StockDashboard /></ManagerRoute>} />
      <Route path="/stock/move" element={<ManagerRoute><StockMove /></ManagerRoute>} />
      {/* SPEC-PRIZE-THEORETICAL-STOCK-PAGE-01 (D-404): 理論在庫(資産額把握・棚卸事前準備。manager+)。 */}
      <Route path="/stock/theoretical" element={<ManagerRoute><PrizeTheoreticalStockPage /></ManagerRoute>} />
      <Route path="/stock/out"     element={<ProtectedRoute><StockOutPage /></ProtectedRoute>} />
      {/* SPEC-ARRIVAL-ADMIN-ONLY-01 (D-899): 「入荷チェック」(管理者向け)は admin/manager 限定。
          ★カードを隠すだけだと URL 直打ちで誰でも入れてしまうため、ここも同じ MANAGER_ROLES で塞ぐ
          (ハブ側のタイル出し分けと同じ配列。2 箇所に書かない)。弾かれた先は /stock(タナサポの入口)。 */}
      <Route path="/stock/arrival" element={<ManagerRoute fallback="/stock"><ArrivalCheckPage /></ManagerRoute>} />
      {/* SPEC-ARRIVAL-PENDING-LIST-01 (D-319): 未検収滞留一覧(ログインのみ・ロール限定しない=SCREEN_GUARD)。 */}
      <Route path="/stock/arrival-pending" element={<ProtectedRoute><ArrivalPendingPage /></ProtectedRoute>} />
      {/* SPEC-STAFF-ARRIVAL-ONE-TAP-WITH-PHOTO-01 (D-834): バイト向けの受取画面。
          ★/stock/arrival(管理者向けの入荷チェック)とは **別の入口**。既存画面は 1 ビットも変えない。
          ★ProtectedRoute で十分(staff もタナサポに入れる = MODULE_ACCESS.stocktake に staff が居る/D-576)。
            入庫そのものの権限は fn_confirm_arrival 側の is_all_store_access() が見る。 */}
      <Route path="/stock/arrival-staff" element={<ProtectedRoute><StaffArrivalPage /></ProtectedRoute>} />
      {/* SPEC-STOCK-LAUNCHER-REDIRECT-02: 旧 Navigate to=/stock は Launcher 由来の /stock/stocktake
          を踏むと /stock へ戻るループ + URL バー揺れの原因。StocktakeTargetPage を直接 mount して
          /stock/stocktake と /stock で同 page を render (両 URL が同等扱い)。 */}
      <Route path="/stock/stocktake" element={<ProtectedRoute><StocktakeTargetPage /></ProtectedRoute>} />
      <Route path="/stock/stocktake/session" element={<ProtectedRoute><StocktakeSessionPage /></ProtectedRoute>} />
      {/* SPEC-STOCKTAKE-STAFF-COUNT-V1 (D-446): 車在庫(スタッフ個人持ち)カウント。個人タブから遷移。 */}
      <Route path="/stock/stocktake/vehicle" element={<ProtectedRoute><StaffStockCount /></ProtectedRoute>} />
      <Route path="/stock/orders" element={<ProtectedRoute><OrderList /></ProtectedRoute>} />
      {/* J-STOCK-OCR-COUNT-TEST-01: 棚卸 OCR カウントテスト (temp、DB なし) */}
      <Route path="/stock/ocr-count-test" element={<ProtectedRoute><OcrCountTestPage /></ProtectedRoute>} />
      {/* SPEC-STOCK-ANNOUNCEMENTS-01: 景品案内ビューア。role_access: [admin, manager, patrol, staff] のため ProtectedRoute (login required only)。 */}
      <Route path="/stock/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/launcher" replace />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  )
}
